import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Avatar from '@mui/material/Avatar';
import { FACEDOWN } from 'constants/gameConstants';

import { avatarInitial, resolveAvatarSrc } from './avatar';
import { BoardCards, HoleCards } from './Cards';
import { formatAmount, formatPlayerDisplayName, formatPot } from './format';
import { GameType, PotGroupPlayerSnapshot } from './types';

import styles from './HandHistory.module.scss';

// Game Chat's avatar treatment: the picture when there is one, the player's
// initial otherwise (MUI swaps to the children on a load error). No stand-in
// picture - a bot with no avatar gets its initial, not a red placeholder.
const rowAvatarSx = {
  width: 26,
  height: 26,
  fontSize: '0.7rem',
  fontWeight: 700,
  bgcolor: 'rgba(255, 255, 255, 0.1)',
  color: 'rgba(255, 255, 255, 0.9)'
};

/**
 * A winning row's five split into what the player held and what the board
 * gave: the hole cards that played, and the community cards that did, in the
 * order the board (the one that paid the row) dealt them. Exactly five cards
 * between them — a hole card that did not play is not shown at all. Undefined
 * unless the row is a shown winner whose whole five is found in their hand and
 * that board; the row then shows the hand as before. Exported for test.
 */
export const getBestFive = (
  player: Pick<PotGroupPlayerSnapshot, 'isWinner' | 'cardsRevealed' | 'cards' | 'winningCards'>,
  board: string[] = []
): { hole: string[]; board: string[] } | undefined => {
  if (!player.isWinner || !player.cardsRevealed || player.winningCards?.length !== 5) {
    return undefined;
  }
  const key = (card: string) => card.trim().toUpperCase();
  const five = new Set(player.winningCards.map(key));
  const held = new Set((player.cards ?? []).map(key));
  const hole = (player.cards ?? []).filter((card) => card && five.has(key(card)));
  const shared = board.filter((card) => card && five.has(key(card)) && !held.has(key(card)));
  return hole.length + shared.length === 5 ? { hole, board: shared } : undefined;
};

export const PlayerResultRow = ({
  player,
  gameType,
  isViewer,
  fallbackAvatar,
  expanded,
  onToggle,
  board
}: {
  player: PotGroupPlayerSnapshot;
  gameType: GameType;
  isViewer?: boolean;
  fallbackAvatar?: string;
  expanded?: boolean;
  onToggle?: () => void;
  /** The board that paid this row (its run's, on a multi-run hand). */
  board?: string[];
}) => {
  const netClass = player.net > 0 ? styles.positive : player.net < 0 ? styles.negative : '';
  // What the player took from THIS pot (Cory, 2026-09-23: "they both profited
  // $10, but they won $32 from the pot"). The net alone reads like the payout
  // and confuses split pots, so a winner shows both: the amount won, then the
  // net underneath (EN-9775 items 10/50, restored by EN-11110 C9).
  const grossWin = player.isWinner && (player.grossWin ?? 0) > 0 ? player.grossWin : undefined;
  const cards = player.cardsRevealed ? player.cards : player.cards?.map(() => FACEDOWN);
  // A winner's best five on the board that paid this row, and only those five:
  // the hole cards that played on the left, then — after a gap — the
  // community cards that completed it. Otherwise the hand as dealt.
  const bestFive = getBestFive(player, board);
  const avatarSrc = resolveAvatarSrc(player.avatar ?? fallbackAvatar);
  const displayName = formatPlayerDisplayName(player.name, isViewer);
  const statusText = player.mucked ? 'MUCK' : player.badge?.replace('_', ' ');
  const statusOnly = Boolean(player.badge || player.mucked) && !player.cardsRevealed;
  // FOLD sits on line 2 so the right edge keeps the amount the player lost
  // (2026-10-12): under the name when there are no cards, centred under the
  // cards when the row shows them (the viewer's own). MUCK — the record shows
  // a mucked hand's cards (EN-10943) — sits as a pill after the amount at the
  // right edge (Jason, 28 Sep), as does any other status on a row with nothing
  // else to show. Other badges on a row that shows cards (SHOW OFF, BUST) stay
  // beside the name.
  const foldStatus = player.badge === 'FOLD' && !player.mucked;
  const trailingStatus = !foldStatus && (player.mucked || statusOnly) ? statusText : undefined;
  const inlineStatus = trailingStatus || foldStatus ? undefined : statusText;
  const foldPill = (
    <span className={`${styles.statusBadge} ${styles.foldStatusBadge}`} data-fold-status=''>
      {statusText}
    </span>
  );
  const result = (
    <span className={styles.playerResult}>
      {grossWin !== undefined && (
        <span className={styles.grossAmount}>Won {formatPot(grossWin, gameType)}</span>
      )}
      {/* Under "Won $X", the net: on a multi-run row, that row's proportional
          share of the seat's hand net (the capture splits it across the
          seat's rows). A loser's row shows the net alone. */}
      {/* Zero included: "$0.00" says the player lost nothing, where a blank
          read as missing (2026-10-12). */}
      {Number.isFinite(player.net) && (
        <span
          className={`${styles.netAmount} ${netClass} ${
            grossWin !== undefined ? styles.netAmountSecondary : ''
          }`}>
          {formatAmount(player.net, gameType)}
        </span>
      )}
    </span>
  );

  return (
    <button
      className={`${styles.playerRow} ${player.isWinner ? styles.playerWinner : ''} ${
        player.folded ? styles.playerFolded : ''
      }`}
      type='button'
      disabled={!onToggle}
      aria-expanded={onToggle ? Boolean(expanded) : undefined}
      onClick={onToggle}>
      <span className={styles.avatar}>
        <Avatar sx={rowAvatarSx} src={avatarSrc} alt=''>
          {avatarInitial(player.name)}
        </Avatar>
        {player.isDealer && <span className={styles.dealerButton}>D</span>}
      </span>

      {/* Two lines (EN-11110 C9). Line 1: the name and the hand rank in full,
          never ellipsised. Line 2: which run paid it, then the cards. The
          amount, then how the hand ended, sit at the right edge across both. */}
      <span className={styles.playerNameBlock} data-row-line='1'>
        <span className={styles.playerName}>{displayName}</span>
        {(player.handRank || player.loLabel) && player.cardsRevealed && (
          <span className={styles.handRank}>
            {/* A low half's row has only its low: no stray " / " before it. */}
            {[player.handRank, player.loLabel].filter(Boolean).join(' / ')}
          </span>
        )}
        {inlineStatus && <span className={styles.inlineStatusBadge}>{inlineStatus}</span>}
      </span>

      {(player.run !== undefined || !statusOnly || foldStatus) && (
        <span className={styles.playerCardsLine} data-row-line='2'>
          {/* Which board paid this row on a multi-run hand (one row per run). */}
          {player.run !== undefined && (
            <span className={`${styles.runPill} ${styles.playerRunPill}`}>Run {player.run}</span>
          )}
          {/* No cards to show: FOLD alone, under the name. */}
          {foldStatus && statusOnly && foldPill}
          {!statusOnly && bestFive ? (
            <>
              {/* A board that played all five leaves no hole card to show. */}
              {bestFive.hole.length > 0 && (
                <span className={styles.playerCards}>
                  <HoleCards cards={bestFive.hole} revealed size='base' />
                </span>
              )}
              {bestFive.board.length > 0 && (
                <span className={styles.playerBoardCards} data-best-five-board=''>
                  <BoardCards cards={bestFive.board} size='base' />
                </span>
              )}
            </>
          ) : !statusOnly && foldStatus ? (
            // The viewer's own folded cards, FOLD centred beneath them.
            <span className={styles.foldedCards}>
              <span className={styles.playerCards}>
                <HoleCards cards={cards} revealed={player.cardsRevealed} size='base' />
              </span>
              {foldPill}
            </span>
          ) : (
            !statusOnly && (
              <span className={styles.playerCards}>
                <HoleCards
                  cards={cards}
                  revealed={player.cardsRevealed}
                  size='base'
                  highlight={player.isWinner ? player.winningCards : undefined}
                />
              </span>
            )
          )}
        </span>
      )}
      {result}
      {trailingStatus && (
        <span className={styles.playerStatus}>
          <span className={styles.statusBadge}>{trailingStatus}</span>
        </span>
      )}

      {player.isWinner && onToggle && (
        <span className={`${styles.rowChevron} ${expanded ? styles.rowChevronOpen : ''}`}>
          <KeyboardArrowDownIcon />
        </span>
      )}
    </button>
  );
};
